import { Model, Factory, trait, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { rand, randPhrase, randNumber, randChanceBoolean } from '@ngneat/falso'
import { filterable } from './utils.js'

import type { SchemaAttributes } from '../types.js'
import type { MockServer } from './server.js'

export type ComplaintTypeData = SchemaAttributes<'ComplaintType'>

export const model = (Model as ModelDefinition<ComplaintTypeData>).extend({
  complaintTypeGroup: belongsTo('complaintTypeGroup'),
  reportingDimension1: belongsTo('listItem'),
  reportingDimension2: belongsTo('listItem'),
})

export const factory = Factory.extend<ComplaintTypeData>({
  name() {
    return randPhrase({ maxCharCount: 50 })
  },
  color() {
    return rand(['#FFFFFF', '#b2babf', '#401197'])
  },
  available_for_boarding() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  archived() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  duration() {
    return randNumber({ min: 15, max: 150 })
  },
  withAvailableForBoarding: trait({
    available_for_boarding: true,
  }),
  withNotArchived: trait({
    archived: false,
  }),
  withComplaintTypeGroup: trait({
    afterCreate(complaintType, server) {
      const complaintTypeGroup = server.create('complaintTypeGroup')
      complaintType.update({ complaintTypeGroup })
    },
  }),
  withReportingDimension1: trait({
    afterCreate(complaintType, server) {
      const reportingDimension1 = server.create(
        'listItem',
        'asReportingDimension1',
      )
      complaintType.update({ reportingDimension1 })
    },
  }),
  withReportingDimension2: trait({
    afterCreate(complaintType, server) {
      const reportingDimension2 = server.create(
        'listItem',
        'asReportingDimension2',
      )
      complaintType.update({ reportingDimension2 })
    },
  }),
})

export function routes(server: MockServer) {
  server.resource('complaintType', { path: '/complaint_type' })

  server.get('/complaint_types', function (schema, request) {
    const complaints = schema.all('complaintType')

    const filtered = filterable(complaints, request, {
      available_for_boarding: (item, value) =>
        item.available_for_boarding === (value === 'true'),
      archived: (item, value) => item.archived === (value === 'true'),
    })

    return filtered
  })
}

export function seeds(server: MockServer) {
  server.createList(
    'complaintType',
    10,
    'withComplaintTypeGroup',
    'withReportingDimension1',
    'withReportingDimension2',
  )
}
